import { vec3, type Vec3 } from './vec3'
import { quat, type Quat } from './quat'
import { mat4, type Mat4 } from './mat4'

/** A rigid pose (+ uniform scale) in camera space. Units: centimetres. */
export interface Pose {
  position: Vec3
  rotation: Quat
  scale: number
}

export const pose = {
  create(): Pose {
    return { position: [0, 0, 0], rotation: [0, 0, 0, 1], scale: 1 }
  },
  clone(p: Pose): Pose {
    return { position: vec3.clone(p.position), rotation: quat.clone(p.rotation), scale: p.scale }
  },
  copy(out: Pose, p: Pose): Pose {
    vec3.copy(out.position, p.position)
    quat.copy(out.rotation, p.rotation)
    out.scale = p.scale
    return out
  },
  toMat4(out: Mat4, p: Pose): Mat4 {
    return mat4.compose(out, p.position, p.rotation, p.scale)
  },
  fromMat4(out: Pose, m: Mat4): Pose {
    mat4.getTranslation(out.position, m)
    quat.fromMat4(out.rotation, m)
    const s: Vec3 = [0, 0, 0]
    mat4.getScale(s, m)
    out.scale = (s[0] + s[1] + s[2]) / 3
    return out
  },
  /** out = a ∘ local (local applied in a's frame). */
  multiply(out: Pose, a: Pose, local: Pose): Pose {
    const tmp: Vec3 = [0, 0, 0]
    vec3.scale(tmp, local.position, a.scale)
    quat.rotateVec3(tmp, a.rotation, tmp)
    const px = a.position[0] + tmp[0]
    const py = a.position[1] + tmp[1]
    const pz = a.position[2] + tmp[2]
    quat.multiply(out.rotation, a.rotation, local.rotation)
    out.position[0] = px
    out.position[1] = py
    out.position[2] = pz
    out.scale = a.scale * local.scale
    return out
  },
}
